/* eslint-disable @typescript-eslint/no-explicit-any */
import { SyncOutlined } from '@ant-design/icons'
import { Affix, Alert, Button, Card, Space, Spin, Tabs } from 'antd'
import TabPane from 'antd/lib/tabs/TabPane'
import {
  ADMIN_MARKETPLACE,
  ADMIN_ROLE,
  BEE,
  BGM,
  CATEGORY_MAN,
  CONTENT,
  DISTRIBUTOR_STAFF,
  EXTERNAL_DISTRIBUTOR,
  FINANCE,
  HUB_MANAGER,
  isAdmin,
  isAdminMarketplace,
  isBGM,
  isMetaCampaignOnly,
  isRoleWarehouseOwnership,
  META_CAMPAIGN,
  MT,
  PO_MANAGER,
  SALE_SUPERVISOR,
} from 'common/config/acl'
import { PICKLIST_ASSIGNMENT } from 'common/config/routes'
import { useAppContext } from 'contexts/AppContext'
import useACL from 'hooks/useACL'
import useConsumerCustomerGroup from 'hooks/useConsumerCustomerGroup'
import useFirstRender from 'hooks/useFirstRender'
import useGetDistributorID from 'hooks/useGetDistributorID'
import useGetWarehouseID from 'hooks/useGetWarehouseIDs'
import { DEFAULT_MAX_PAGE_SIZE, DEFAULT_PAGE } from 'hooks/usePaging'
import { Distributor } from 'interfaces/distributor'
import { IPicklistFilter, Picklist } from 'interfaces/picklist'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import dynamic from 'next/dynamic'
import router from 'next/router'
import { useSession } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import stringify from 'qs/lib/stringify'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axiosInstance from 'utils/axiosInstance'
import {
  defaultStringifyOption,
  getResponseData,
  getResponseErrorMessage,
  isIndia,
  safeNumberParse,
} from 'utils/commonUtils'
import {
  DAILY_SALES_REPORT_TITLE,
  PICKLIST_STATUS_ALL,
  PICKLIST_STATUS_CHECKING,
  PICKLIST_STATUS_COMPLETED,
  PICKLIST_STATUS_CREATED,
  PICKLIST_STATUS_PICKING,
  PICKLIST_STATUS_VN_MAPPING,
} from 'utils/picklist'

const PicklistFiltersIndia = dynamic(
  () => import('../AssignmentIndia/PicklistFiltersIndia')
)
const PicklistTableIndia = dynamic(
  () => import('../AssignmentIndia/PicklistTableIndia')
)
const PicklistFilters = dynamic(() => import('./PicklistFilters'))
const PicklistTable = dynamic(() => import('./PicklistTable'))
const PrintButtons = dynamic(() => import('./PrintButtons'))
const CardPageTitle = dynamic(() => import('components/CardPageTitle'))
const ErrorAlert = dynamic(() => import('components/ErrorAlert'))
const NewPagination = dynamic(() => import('components/NewPagination'))
const DeliveryDrawer = dynamic(
  () => import('containers/Rider/Delivery/DeliveryDrawer')
)

export const defaultFilters = {
  orderBy: 'updatedAt desc',
  page: DEFAULT_PAGE,
  perPage: DEFAULT_MAX_PAGE_SIZE,
  statuses: isIndia
    ? [
        PICKLIST_STATUS_CHECKING,
        PICKLIST_STATUS_CREATED,
        PICKLIST_STATUS_PICKING,
      ]
    : [],
  includeOrder: true,
  includeProduct: true,
  includeWarehouse: true,
  includeOrderItem: true,
  includeDistributor: true,
  createdAtFromDate: isIndia
    ? moment().subtract(1, 'weeks').add(1, 'day').startOf('day').toISOString()
    : moment().subtract(6, 'months').add(1, 'day').startOf('day').toISOString(),
  createdAtToDate: moment().endOf('day').toISOString(),
}

const PickListsContainer = () => {
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [picklists, setPicklists] = useState<Picklist[]>([])
  const [total, setTotal] = useState<number>(null)
  const [dataSource, setDataSource] = useState<Picklist[]>([])
  const [filters, setFilters] = useState<IPicklistFilter>({})
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [dataPicklistSelected, setDataPicklistSelected] = useState<Picklist[]>(
    []
  )
  const { data: session } = useSession()
  const { userRoles } = useACL()
  const [distributor, setDistributor] = useState<Distributor[]>(undefined)
  const [visible, setVisible] = useState(false)
  const translate = useTranslations()
  const { distributorID, finish } = useGetDistributorID()
  const { warehouseIDs } = useGetWarehouseID()
  const [tab, setTab] = useState<string>()
  const isFirstRender = useFirstRender()
  const {
    state: { warehouses, distributors },
  } = useAppContext()

  const isRoleAdminMarketplace = isAdminMarketplace(userRoles)

  const isRoleWarehouseOwner = useMemo(() => {
    return isRoleWarehouseOwnership(userRoles)
  }, [userRoles])
  const isHubManager = useMemo(() => {
    return userRoles.some((r) => r === HUB_MANAGER)
  }, [userRoles])
  const currentUserEmail = useMemo(() => {
    return get(session, 'user.email') as string
  }, [session?.user])

  const isRoleBGM = isBGM(userRoles)
  const isRoleAdmin = isAdmin(userRoles)
  const isMetaCampaign = isIndia && isMetaCampaignOnly(userRoles)
  const {
    consumerGroupID,
    loading: consumerGroupLoading,
    error: consumerGroupError,
  } = useConsumerCustomerGroup(isMetaCampaign)

  const isExternalDistributor = useMemo(() => {
    return userRoles.some((r) => r === EXTERNAL_DISTRIBUTOR)
  }, [userRoles])
  const isDistributorStaff = useMemo(() => {
    return userRoles.some((r) => r === DISTRIBUTOR_STAFF)
  }, [userRoles])
  const visibleInvoice = useMemo(() => {
    return userRoles.some(
      (r) => r === EXTERNAL_DISTRIBUTOR || r === ADMIN_ROLE || r === BGM
    )
  }, [userRoles])

  useEffect(() => {
    const getDistributor = async () => {
      try {
        const params = stringify({
          ownerEmail: currentUserEmail,
        })
        const res = await axiosInstance.get(`/v1/distributors?${params}`)
        const distributors = getResponseData<Distributor[]>(res)
        setDistributor(distributors)
      } catch (error) {
        setDistributor(undefined)
      }
    }

    if (isExternalDistributor && currentUserEmail && isIndia) {
      getDistributor()
    } else {
      setDistributor(undefined)
    }
  }, [isExternalDistributor, currentUserEmail, isIndia])

  const getPicklists = useCallback(
    async (distributorID?: string, warehouseIDs?: string[]) => {
      if (isRoleWarehouseOwner && warehouses.length === 0) return
      try {
        setLoading(true)
        setError('')
        if (isExternalDistributor && !distributorID) {
          throw new Error(
            translate('common.distributorValidMsg', { email: currentUserEmail })
          )
        }
        const { createdAtFromDate, createdAtToDate, statuses, ...nextFilters } =
          filters

        const ids = isRoleWarehouseOwner
          ? warehouses.map((item) => item.id)
          : warehouseIDs
        const params = stringify(
          distributorID
            ? {
                ...defaultFilters,
                ...nextFilters,
                statuses: statuses ?? undefined,
                createdAtFromDate: createdAtFromDate ?? undefined,
                createdAtToDate: createdAtToDate ?? undefined,
                distributorID,
              }
            : {
                ...defaultFilters,
                warehouseIDs: ids,
                ...nextFilters,
                statuses: statuses ?? undefined,
                createdAtFromDate: createdAtFromDate ?? undefined,
                createdAtToDate: createdAtToDate ?? undefined,
              },
          defaultStringifyOption
        )

        const response = await axiosInstance.get(`/v1/picklists?${params}`)
        const data = getResponseData<Picklist[]>(response)
        const total = response?.data?.metadata?.total

        setSelectedIds([])
        setDataPicklistSelected([])

        setPicklists(data)
        setTotal(total)
      } catch (error) {
        const err = getResponseErrorMessage(error)
        setError(
          typeof err === 'string'
            ? err
            : translate('common.getMessage.error', {
                name: translate(`common.${PICKLIST_ASSIGNMENT}`),
              })
        )
      } finally {
        setLoading(false)
      }
    },
    [
      filters,
      selectedIds,
      distributorID,
      warehouseIDs,
      warehouses,
      isRoleWarehouseOwner,
    ]
  )

  useEffect(() => {
    if (picklists?.length > 0) {
      if (distributor?.length > 0) {
        const newData = picklists.map((i) => {
          const dataDistributor = distributor?.filter(
            ({ id }) => i?.distributorID === id
          )
          if (dataDistributor?.length > 0) {
            return { ...i, distributor: { ...dataDistributor[0] } }
          } else {
            return i
          }
        })
        setDataSource(newData)
      } else setDataSource(picklists)
    } else setDataSource([])
  }, [picklists, distributor])

  const checkGetPicklists = () => {
    if (distributorID) {
      getPicklists(distributorID)
      return
    }
    if (isRoleBGM && warehouseIDs?.length > 0) {
      getPicklists(undefined, warehouseIDs)
      return
    }
    if (!isRoleBGM && !distributorID) {
      getPicklists()
    }
  }

  useEffect(() => {
    if (!isFirstRender && !isEmpty(filters) && finish) {
      checkGetPicklists()
    }
  }, [
    isFirstRender,
    filters,
    distributorID,
    warehouseIDs,
    finish,
    warehouses,
    isRoleWarehouseOwner,
  ])

  useEffect(() => {
    // Meta campaign users are always scoped to the Consumer customer type, so
    // wait for it to resolve instead of fetching unscoped data
    if (isMetaCampaign && !consumerGroupID) return

    if (!isEmpty(router.query)) {
      const page = safeNumberParse(get(router, 'query.page'), DEFAULT_PAGE)
      const perPage = safeNumberParse(get(router, 'query.perPage'), 100)

      delete router.query.province

      setFilters({
        ...router.query,
        page,
        perPage,
        ...(isMetaCampaign && { agentTypes: [consumerGroupID] }),
      })
    } else {
      const params = stringify(
        {
          ...defaultFilters,
        },
        defaultStringifyOption
      )
      router.push(`${router.pathname}?${params}`)
    }
  }, [router.query, isMetaCampaign, consumerGroupID])

  const handlePageChange = async (e, i) => {
    const params = stringify(
      { ...router.query, ...filters, perPage: i, page: e },
      defaultStringifyOption
    )
    router.push(`${router.pathname}?${params}`)
  }

  const handleCompletePicklist = async (data: Picklist[]) => {
    try {
      if (data.length === 0) return

      await axiosInstance.post(`/v1/picklists/statuses`, {
        status: PICKLIST_STATUS_COMPLETED,
        ids: data.map(({ id }) => id),
      })
      checkGetPicklists()
    } catch (error) {
      console.log('Failed to complete picklist!', error)
    }
  }

  const onChange = (key: string) => {
    const statuses = key === PICKLIST_STATUS_ALL ? undefined : key
    const params = stringify(
      { ...router.query, statuses, page: 1 },
      defaultStringifyOption
    )
    router.push(`${router.pathname}?${params}`)
    setTab(key)
  }
  // const onChange = (key: string) => {
  //   const statuses = key === TRADE_ORDER_STATUS_ALL ? undefined : key
  //   const params = stringify(
  //     { ...router.query, statuses, statusType: undefined, page: 1 },
  //     defaultStringifyOption
  //   )
  //   router.push(`${router.pathname}?${params}`)
  //   setTab(key)
  // }

  const renderPrintButtons = (selectedIds: string[], picklists: Picklist[]) => {
    if (selectedIds.length > 100) {
      return (
        <Alert
          type="warning"
          message={translate('picklist.printErrorMessage')}
          closable
          onClose={() => setSelectedIds([])}
        />
      )
    }
    return (
      <PrintButtons
        picklists={picklists}
        selectedIds={selectedIds}
        onRefresh={() => checkGetPicklists()}
        loading={(loading) => setLoading(loading)}
        handleCompletePicklist={handleCompletePicklist}
        isRoleAdmin={isRoleAdmin}
        setSelectedIds={setSelectedIds}
      />
    )
  }
  const renderPrintButtonsIndia = (
    selectedIds: string[],
    picklists: Picklist[],
    isExternalDistributor: boolean,
    filters: IPicklistFilter
  ) => {
    if (selectedIds.length > 100) {
      return (
        <Alert
          type="warning"
          message={translate('picklist.printErrorMessage')}
          closable
          onClose={() => setSelectedIds([])}
        />
      )
    }
    // Always render PrintButtons even when no items are selected
    // This allows the component to stay mounted and handle state changes
    return (
      <Space>
        <PrintButtons
          picklists={picklists}
          selectedIds={selectedIds}
          onRefresh={() => checkGetPicklists()}
          loading={(loading) => setLoading(loading)}
          isExternalDistributor={isExternalDistributor || isDistributorStaff}
          filters={filters}
          setSelectedIds={setSelectedIds}
        />
      </Space>
    )
  }

  return isIndia ? (
    <div className="relative w-full">
      <Spin spinning={loading || consumerGroupLoading}>
        <Card
          title={
            <CardPageTitle
              title={
                isMetaCampaign
                  ? DAILY_SALES_REPORT_TITLE
                  : 'Picklist Management'
              }
            />
          }
          extra={
            <Space>
              {!isHubManager && !isMetaCampaign && (
                <Button type="primary" onClick={() => setVisible(true)}>
                  Create new delivery
                </Button>
              )}
              <Button
                icon={<SyncOutlined />}
                onClick={() => checkGetPicklists()}
              >
                Refresh
              </Button>
            </Space>
          }
          className="border-none"
        >
          <div className="w-full ">
            {error && <ErrorAlert error={error} onClose={() => setError('')} />}
            {isMetaCampaign && consumerGroupError && (
              <Alert
                type="error"
                message="Unable to load the Consumer customer type. Please refresh or contact support."
              />
            )}

            <PicklistFiltersIndia
              hideCustomerType={isMetaCampaign}
              filters={filters}
              setSelectedIds={setSelectedIds}
              isRoleForWarehouseOwnerShip={isRoleWarehouseOwner}
              warehouses={warehouses}
              isRoleBGM={isRoleBGM}
            />
          </div>
        </Card>
        <Affix offsetTop={10}>
          <Card className="m-3">
            <div>
              <Space className=" mb-2 w-full flex justify-between">
                <Space>
                  <div>{total ? `Total ${total} items` : 'Total 0 items'}</div>
                  <NewPagination
                    totalPage={total}
                    showQuickJumper={true}
                    defaultPageSize={100}
                    className="mb-2"
                    onChange={handlePageChange}
                    currentPage={filters?.page ?? defaultFilters.page}
                    pageSize={filters?.perPage ?? defaultFilters.perPage}
                  />
                </Space>

                {!isMetaCampaign &&
                  renderPrintButtonsIndia(
                    selectedIds,
                    dataPicklistSelected,
                    isExternalDistributor || isDistributorStaff,
                    filters
                  )}
              </Space>
              <PicklistTableIndia
                readOnly={isMetaCampaign}
                dataSource={dataSource}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                setDataPicklistSelected={setDataPicklistSelected}
                dataPicklistSelected={dataPicklistSelected}
              />
            </div>
          </Card>
        </Affix>

        {/* print container */}
      </Spin>

      <DeliveryDrawer
        visible={visible}
        onCancel={() => setVisible(false)}
        onRefresh={getPicklists}
        translate={translate}
        distributors={distributors}
        isRoleWarehouseOwner={isRoleWarehouseOwner}
        warehouses={warehouses}
        warehouseID={filters?.warehouseID}
      />
    </div>
  ) : (
    <div className="relative w-full z-10">
      <Spin spinning={loading}>
        <Card
          title={
            <CardPageTitle title={translate(`common.${PICKLIST_ASSIGNMENT}`)} />
          }
          extra={
            <Space>
              <Button
                icon={<SyncOutlined />}
                onClick={() => checkGetPicklists()}
              >
                {translate('common.refresh')}
              </Button>
            </Space>
          }
        >
          <div className="w-full space-y-4">
            {error && <ErrorAlert error={error} onClose={() => setError('')} />}

            <PicklistFilters
              isRoleBGM={isRoleBGM}
              distributors={distributors}
              warehouses={warehouses}
              isRoleForWarehouseOwnerShip={isRoleWarehouseOwner}
              setTab={(tab) => setTab(tab)}
            />

            {/* <div className="my-4 italic">
              <span>
                {total ? total : 0} {translate('common.recordsFound')}
              </span>
            </div> */}
          </div>
        </Card>
        <Card className="m-3">
          <Tabs activeKey={tab} onChange={onChange}>
            {Object.entries(PICKLIST_STATUS_VN_MAPPING).map(([key]) => {
              return <TabPane tab={translate(`picklist.${key}`)} key={key} />
            })}
          </Tabs>
          <Space className="mb-2 w-full flex justify-between">
            <div className="flex gap-2">
              {translate('common.totalItems', {
                items: total || 0,
              })}
              <NewPagination
                totalPage={total}
                showQuickJumper
                defaultPageSize={filters?.perPage ?? defaultFilters?.perPage}
                className="mb-2"
                onChange={handlePageChange}
                currentPage={filters?.page ?? defaultFilters.page}
                pageSize={filters?.perPage ?? defaultFilters.perPage}
              />
            </div>

            {renderPrintButtons(selectedIds, dataSource)}
          </Space>
          <div>
            <PicklistTable
              dataSource={dataSource}
              filters={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onCompletePicklist={handleCompletePicklist}
              translate={translate}
              getPicklists={getPicklists}
              visibleInvoice={visibleInvoice}
              isRoleAdminMarketplace={isRoleAdminMarketplace}
            />
          </div>
        </Card>
      </Spin>
    </div>
  )
}

PickListsContainer.auth = {
  acceptedRoles: [
    ADMIN_ROLE,
    BGM,
    PO_MANAGER,
    FINANCE,
    CONTENT,
    EXTERNAL_DISTRIBUTOR,
    ADMIN_MARKETPLACE,
    DISTRIBUTOR_STAFF,
    SALE_SUPERVISOR,
    HUB_MANAGER,
    CATEGORY_MAN,
    BEE,
    MT,
    META_CAMPAIGN,
  ],
  redirectPath: '/',
}

export default PickListsContainer
